/**
 * DiscardChangesDialog — shared confirmation surface used by every
 * ConnectionList edit dialog to guard against losing unsaved edits on close.
 *
 * Rendered as a sibling of the primary edit dialog. Its own `onOpenChange`
 * (Esc / backdrop) collapses to `onKeepEditing` — discarding always requires
 * an explicit button click, matching the pattern the managed edit dialog
 * originally shipped with.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@coreai/fluentui-react';

export interface DiscardChangesDialogProps {
  open: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
  /** Optional override for the dialog body. Defaults to the generic copy. */
  message?: string;
}

const DEFAULT_MESSAGE =
  'You have unsaved changes to this connection. Closing now will discard them.';

export const DiscardChangesDialog = ({
  open,
  onKeepEditing,
  onDiscard,
  message = DEFAULT_MESSAGE,
}: DiscardChangesDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={(_, data) => {
      if (!data.open) onKeepEditing();
    }}
  >
    <DialogSurface>
      <DialogBody>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>{message}</DialogContent>
        <DialogActions>
          <Button appearance="secondary" onClick={onKeepEditing}>
            Keep editing
          </Button>
          <Button appearance="primary" onClick={onDiscard}>
            Discard changes
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  </Dialog>
);

DiscardChangesDialog.displayName = 'DiscardChangesDialog';
